<template>
  <button-with-menu
    :icon="isVideoMuted ? 'videoCameraOffM' : 'videoCameraM'"
    :loading="loading"
    :is-active="!isVideoMuted"
    :color-type="colorType"
    :disabled="disabled"
    :with-menu="!!devices.videoInput.size && !disabled"
    :close-on-click-inside="false"
    data-test="call-session-camera-button"
    :button-title="cameraButtonTitle"
    @button-click="toggleMuteVideo()"
  >
    <template
      v-if="!isCameraPermissionGranted"
      #badge="{}"
    >
      <span>!</span>
    </template>
    <div class="mb-4 border border-bor-nav rounded-12 py-4 bg-elev-context-menu-bg w-[358px]">
      <p class="px-4 text-fg-base-fg3 font-light text-1624">
        {{ $t('soundAndVideo.camera') }}
      </p>
      <template v-if="isWebcamAvailable">
        <wui-accordion v-model="isMenuWithVideoDevicesOpen">
          <template #header>
            <div
              class="flex justify-between items-center px-4 cursor-pointer"
              data-test="toggle-video-devices-menu-elem"
            >
              <p class="line-clamp-1 text-1324 text-fg-base-fg3 my-4">
                {{ activeWebcamLabel }}
              </p>
              <wui-icon
                name="expand"
                class="text-fg-base-fg22! h-4! w-4! transition-all"
                :class="{
                  'rotate-180': isMenuWithVideoDevicesOpen,
                  'rotate-0': !isMenuWithVideoDevicesOpen,
                }"
              />
            </div>
          </template>
          <template #content>
            <div
              class="py-2 border-y border-y-bor-nav bg-bg-base-bg3"
              data-test="select-video-devices-menu"
            >
              <div
                v-for="[deviceId, device] in devices.videoInput"
                :key="deviceId"
                class="grid truncate relative text-fg-base-fg3 text-1324 cursor-pointer hover:bg-surf-neut-soft-hov border border-transparent py-1 pl-6 pr-4 transition-all"
                @click.stop="onCameraChoose(deviceId)"
              >
                <wui-icon
                  v-if="deviceId === activeWebcamId"
                  name="expand"
                  class="absolute -rotate-90 left-1 top-2 h-4! w-4!"
                />
                <p
                  class="truncate"
                  :title="device.label"
                >
                  {{ device.label }}
                </p>
              </div>
            </div>
          </template>
        </wui-accordion>
      </template>
      <template v-else-if="isCameraPermissionGranted">
        <div class="my-4 px-4 line-clamp-1 text-1324 text-fg-base-fg22">
          {{ $t('soundAndVideo.noAvailableDevices') }}
        </div>
      </template>
      <template v-else>
        <div
          class="my-4 px-4 line-clamp-1 text-1324 text-fg-base-fg3 cursor-pointer"
          @click="toggleMuteVideo()"
        >
          {{ $t('soundAndVideo.allowCamAccess') }}
        </div>
      </template>
      <div class="mt-4 px-4">
        <background-blur-controls compact />
      </div>
    </div>
  </button-with-menu>
</template>

<script setup lang="ts">
import { useRenderNotifier, WuiAccordion, WuiIcon } from '@wui/common-library'
import { storeToRefs } from 'pinia'
import { ref, computed, toRefs, watch, onBeforeMount, onMounted, onBeforeUnmount } from 'vue'

import {
  BackgroundBlurControls,
  loadWatchConferenceJoinForWebcamKeyframes,
  watchBackgroundBlurPreference,
} from '@/features/background-blur'

import { useCallSessionRoomStore } from '@/entities/call-session'
import { useStoreConferenceMediaStatuses, useModeratedConferenceParticipantsSignalsStore } from '@/entities/conference-participant'
import { useStoreMediaDevices } from '@/entities/media-devices'
import { RTCSessionFacade } from '@/entities/webrtc'

import { ButtonWithMenu } from '@/shared/components/buttons'
import { useLocalization } from '@/shared/composables/localization'

import { switchCameraInConferenceSession, switchCameraInPrivateSession } from '../lib'

const props = withDefaults(defineProps<{
  session: RTCSessionFacade,
  cannotEnableVideo?: boolean
  disabled?: boolean
}>(), {
  cannotEnableVideo: false,
  disabled: false,
})

const emit = defineEmits<{
  (e: 'toggle-mute', value: boolean): void
  (e: 'try-to-unmute'): void
}>()

const storeMediaDevices = useStoreMediaDevices()
const { devices, activeWebcamId, isCameraPermissionGranted, isWebcamAvailable } = storeToRefs(storeMediaDevices)
const storeConferenceMediaStatuses = useStoreConferenceMediaStatuses()
const { notifyError } = useRenderNotifier()
const { t } = useLocalization()
const { getIsAllowedVideoEnabling, removeEnablingMediaRequest } = useModeratedConferenceParticipantsSignalsStore()

const { cannotEnableVideo, session, disabled } = toRefs(props)

const isMenuWithVideoDevicesOpen = ref(true)

const room = computed(() => useCallSessionRoomStore().getRoom(session.value.sessionId))
const pServed = computed(() => room.value?.pServedUser)

const activeWebcamLabel = computed(() => {
  return Array.from(devices.value.videoInput.values()).find((device) => {
    return device.deviceId === activeWebcamId.value
  })?.label
})

const isVideoMuted = computed(() => {
  if (room.value?.isGroup && pServed.value) {
    const mediaStatuses = storeConferenceMediaStatuses.getParticipantMediaStatus(props.session.callId.value)
    return Boolean(mediaStatuses?.isVideoMuted)
  }
  return !props.session.isVideoSharing.value
})

const cameraButtonTitle = computed(() => {
  if (disabled.value) {
    return t('common.isOnHold')
  }

  return isVideoMuted.value
    ? `${t('calls.turnOnVideo')} (${t('keys.W')})`
    : `${t('calls.turnOffVideo')} (${t('keys.W')})`
})

const colorType = computed(() => {
  if (isVideoMuted.value && cannotEnableVideo.value && !isVideoAllowedByModerator.value) {
    return 'error'
  }

  return undefined
})

const isVideoAllowedByModerator = computed(() => getIsAllowedVideoEnabling(pServed.value, session.value.callId.value))

const loading = ref(false)

const toggleMuteVideo = async () => {
  if (disabled.value) {
    return
  }

  try {
    loading.value = true
    if (room.value?.isGroup) {
      if (isVideoMuted.value && cannotEnableVideo.value && !isVideoAllowedByModerator.value) {
        emit('try-to-unmute')

        return
      }

      await switchCameraInConferenceSession()
    } else {
      await switchCameraInPrivateSession()
    }
  } catch (error) {
    notifyError(t((error as Error).message))
  } finally {
    loading.value = false
  }
}

const onCameraChoose = async (deviceId: string) => {
  activeWebcamId.value = deviceId
}

watch(isVideoMuted, (v) => {
  if (!v) {
    removeEnablingMediaRequest(pServed.value, session.value.callId.value)
  }

  emit('toggle-mute', v)
})

onBeforeMount(() => {
  emit('toggle-mute', isVideoMuted.value)
})

let stopBlurPreferenceWatch: (() => void) | undefined
let stopJoinKeyframeWatch: (() => void) | undefined

onMounted(() => {
  stopBlurPreferenceWatch = watchBackgroundBlurPreference()
  void loadWatchConferenceJoinForWebcamKeyframes().then((watchConferenceJoin) => {
    stopJoinKeyframeWatch = watchConferenceJoin()
  })
})

onBeforeUnmount(() => {
  stopBlurPreferenceWatch?.()
  stopJoinKeyframeWatch?.()
})
</script>
